import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { fetchUserAbsences } from "../../../api/user";
import { exportAbsencePDF } from "../../../api/absence/absenece";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { PMNotice } from "../../utils/notifications";
import {
  ClockIcon,
  CalendarIcon,
  UserIcon,
  DocumentTextIcon,
  PencilIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { dateToString, ItemStatus } from "../../Utils";

export const AbsencesStacklist = ({ user, period }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [exportingPDFId, setExportingPDFId] = useState(null);
  // Use same role check as parent component (absence:read) to determine admin access
  const isAdmin = useUserHasAccess({ role: "absence", action: "read" });
  const currentUser = useSelector((state) => state.session.currentUser);
  const [data, setData] = useState(null);
  const lastUserIdRef = useRef(null);
  const lastPeriodRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const navigate = useNavigate();

  // If admin and user is selected, use that user's ID, otherwise use current user's ID
  const user_id = isAdmin && user?.id ? user.id : currentUser?.id;

  const getStats = async () => {
    if (!user_id) return;
    
    setIsLoading(true);
    setError(false);
    try {
      const params = {};
      if (period?.from) params.date_from = period.from;
      if (period?.to) params.date_too = period.to;
      
      const response = await fetchUserAbsences(user_id, params);
      if (response.status !== 200) {
        throw new Error(`Server error: ${response.status}`);
      }
      // Ensure data is always an array, even if empty
      const absencesData = Array.isArray(response?.data) ? response.data : [];
      setData(absencesData);
      lastUserIdRef.current = user_id;
      lastPeriodRef.current = period ? JSON.stringify(period) : null;
      hasLoadedRef.current = true;
    } catch (err) {
      console.error("Error fetching absences:", err);
      toast.error("Something went wrong when trying to load the absences");
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Fetch absences when user_id or period changes or on initial load
    const periodKey = period ? JSON.stringify(period) : null;
    if (user_id && (user_id !== lastUserIdRef.current || periodKey !== lastPeriodRef.current || !hasLoadedRef.current)) {
      getStats();
    }
  }, [user_id, period]); // Dependency array ensures this runs when user_id or period changes

  const AbsenceCard = ({ item }) => {
    return (
      <div
        className="shadow sm:overflow-hidden sm:rounded-md hover:shadow-lg transition-shadow duration-200"
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div
                className="flex-shrink-0 cursor-pointer hover:scale-105 transition-transform duration-200"
                onClick={() => navigate("/dashboard/absences/" + item?.id)}
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <div className="flex items-center space-x-3">
                  <h3
                    className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors duration-200"
                    onClick={() => navigate("/dashboard/absences/" + item?.id)}
                  >
                    {item?.displayTitle || item?.absence_type?.displayTitle || "Fravær"}
                  </h3>
                  <ItemStatus item={item} />
                </div>
                {item?.absence_number && (
                  <p className="text-sm text-gray-600">#{item.absence_number}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Absence Type */}
            {item?.absence_type && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <DocumentTextIcon className="w-4 h-4 mr-1" />
                  Fraværstype
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.absence_type.displayTitle || "-"}
                </dd>
              </div>
            )}

            {/* Date Range */}
            {item?.date_from && item?.date_too && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  Periode
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dateToString(item.date_from)} - {dateToString(item.date_too)}
                </dd>
              </div>
            )}

            {/* Duration */}
            {item?.total_days != null && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  Varighet
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.total_days} {item.total_days === 1 ? "dag" : "dager"}
                </dd>
              </div>
            )}

            {/* User */}
            {item?.user && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <UserIcon className="w-4 h-4 mr-1" />
                  Ansatt
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.user.displayTitle || item.user.name || "-"}
                </dd>
              </div>
            )}

            {/* Status */}
            {item?.status && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900 capitalize">
                  {item.status}
                </dd>
              </div>
            )}

            {/* Created Date */}
            {item?.created_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <ClockIcon className="w-4 h-4 mr-1" />
                  Opprettet
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dateToString(item.created_at)}
                </dd>
              </div>
            )}
          </div>

          {/* Description */}
          {item?.description && (
            <div className="mt-6">
              <dt className="text-sm font-medium text-gray-500">Beskrivelse</dt>
              <dd
                className="mt-1 text-sm text-gray-900 line-clamp-3"
                dangerouslySetInnerHTML={{ __html: item.description }}
              />
            </div>
          )}

          {/* Quick Actions */}
          <div className="mt-6 flex items-center justify-end space-x-2">
            {(item.accepted || item.status === "approved") && (
              <button
                onClick={async () => {
                  if (exportingPDFId === item.id) return; // Prevent multiple clicks
                  
                  setExportingPDFId(item.id);
                  try {
                    const result = await exportAbsencePDF(item.id);
                    if (result.success && result.data) {
                      // Check if result.data is a Blob
                      let blob;
                      if (result.data instanceof Blob) {
                        blob = result.data;
                      } else {
                        blob = new Blob([result.data], { type: 'application/pdf' });
                      }
                      
                      // Validate PDF blob
                      if (blob.size < 100) {
                        throw new Error("PDF-filen er for liten eller ugyldig");
                      }
                      
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `fravær_${item.id}_${item.date_from}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      toast.success("PDF ble eksportert");
                    } else {
                      const errorMsg = result.errors?.join(", ") || "Kunne ikke eksportere PDF";
                      toast.error(errorMsg);
                      console.error("PDF export error:", result);
                    }
                  } catch (error) {
                    console.error("Error exporting PDF:", error);
                    toast.error(error.message || "Kunne ikke eksportere PDF");
                  } finally {
                    setExportingPDFId(null);
                  }
                }}
                disabled={exportingPDFId === item.id}
                className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  exportingPDFId === item.id
                    ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                    : "text-gray-700 bg-white hover:bg-gray-50"
                }`}
                title={exportingPDFId === item.id ? "Eksporterer..." : "Eksporter PDF"}
              >
                {exportingPDFId === item.id ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-1"></div>
                    Eksporterer...
                  </>
                ) : (
                  <>
                    <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                    PDF
                  </>
                )}
              </button>
            )}
            <button
              onClick={() => navigate("/dashboard/absences/" + item.id)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PencilIcon className="h-4 w-4 mr-1" />
              Rediger
            </button>
          </div>
        </div>
      </div>
    );
  };

  const Stats = () => {
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <div className="text-lg">Ingen fravær funnet</div>
          <div className="text-sm">Det er ingen registrerte fravær for denne ansatten</div>
        </div>
      );
    }

    return (
      <div className="space-y-4 mt-5">
        {data.map((item) => (
          <AbsenceCard key={item?.id} item={item} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
      <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
            {isLoading ? (
              <>
                <div className="bg-white border rounded-lg shadow-sm p-6 animate-pulse">
                  <div className="flex items-center space-x-3">
                    <div className="h-4 w-4 bg-gray-200 rounded"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                    </div>
                    <div className="flex space-x-2">
                      <div className="h-8 w-16 bg-gray-200 rounded"></div>
                      <div className="h-8 w-8 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
                <div className="bg-white border rounded-lg shadow-sm p-6 animate-pulse">
                  <div className="flex items-center space-x-3">
                    <div className="h-4 w-4 bg-gray-200 rounded"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                    </div>
                    <div className="flex space-x-2">
                      <div className="h-8 w-16 bg-gray-200 rounded"></div>
                      <div className="h-8 w-8 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
              </>
            ) : error ? (
              <div className="text-center py-8 text-red-500">
                <div className="text-lg">Kunne ikke laste inn fravær</div>
              </div>
            ) : (
              <Stats />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
